import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../../utils/i18n';
import { useLanguage } from '../../contexts/LanguageContext';
import { Phone, Mail, MapPin, Shield, Activity, AlertCircle } from 'lucide-react';

interface FooterProps {
  variant?: 'frontend' | 'auth' | 'admin';
}

const Footer: React.FC<FooterProps> = ({ variant = 'frontend' }) => {
  const { t } = useTranslation('footer');
  const { isRTL } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [ripplePosition, setRipplePosition] = useState<{ [key: number]: { x: number; y: number } }>({});
  const socialIconsRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isVisible) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (socialIconsRef.current) {
      observer.observe(socialIconsRef.current);
    }

    return () => {
      if (socialIconsRef.current) {
        observer.unobserve(socialIconsRef.current);
      }
    };
  }, [isVisible]);

  const handleRipple = (index: number, e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setRipplePosition({ ...ripplePosition, [index]: { x, y } });
    setTimeout(() => {
      const newRipple = { ...ripplePosition };
      delete newRipple[index];
      setRipplePosition(newRipple);
    }, 600);
  };

  if (variant === 'auth' || variant === 'admin') {
    return null; // No footer for auth or admin pages
  }

  return (
    <>
      <style>
        {`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          @keyframes ripple {
            0% {
              width: 0;
              height: 0;
              opacity: 0.6;
            }
            100% {
              width: 300px;
              height: 300px;
              opacity: 0;
            }
          }
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
              transform: scale(1);
            }
            50% {
              opacity: 0.8;
              transform: scale(1.02);
            }
          }
          @keyframes scaleIn {
            from {
              opacity: 0;
              transform: scale(0.8);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }
          .zellige-pattern {
            background-image: url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M40 0l10 30 30 10-30 10-10 30-10-30-30-10 30-10z' fill='%23ffffff' fill-opacity='0.03'/%3E%3C/svg%3E");
          }
          .animate-fade-in-up {
            animation: fadeInUp 0.6s ease-out forwards;
          }
          .animate-scale-in {
            animation: scaleIn 0.5s ease-out forwards;
          }
          .animate-pulse-custom {
            animation: pulse 3s ease-in-out infinite;
          }
          .sr-only {
            position: absolute;
            width: 1px;
            height: 1px;
            padding: 0;
            margin: -1px;
            overflow: hidden;
            clip: rect(0, 0, 0, 0);
            white-space: nowrap;
            border-width: 0;
          }
          .glass-card {
            background: rgba(255, 255, 255, 0.05);
            backdrop-filter: blur(8px);
            -webkit-backdrop-filter: blur(8px);
            border: 1px solid rgba(255, 255, 255, 0.1);
          }
        `}
      </style>
      {/* Skip to Footer Link */}
      <a
        href="#footer-main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[9999] focus:px-4 focus:py-2 focus:bg-blue-600 focus:text-white focus:rounded-md focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-yellow-400"
      >
        {t('skipToFooter')}
      </a>
      <footer
        className="rebranded-footer relative bg-[#0a2540] overflow-hidden text-white"
        role="contentinfo"
        aria-label="Informations supplémentaires et liens utiles"
      >
        {/* Zellige Background Overlay */}
        <div className="absolute inset-0 zellige-pattern opacity-60"></div>

        {/* Gradient Overlay for Depth */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a2540]/50 via-transparent to-[#0a2540]/80"></div>

        <div className="footer-container relative z-10 pt-16 pb-8 px-6 md:px-8 lg:px-12 max-w-7xl mx-auto">
          {/* Header Section with Brand Text and Logos */}
          <div className="footer-header mb-12 flex flex-col items-center lg:flex-row lg:justify-between lg:items-stretch gap-10">
            <div className={`footer-brand flex flex-col justify-center items-center md:items-center gap-0`}>
              <div className={`brand-info flex flex-col ${isRTL ? 'md:text-right' : 'md:text-left'} text-center`}>
                <h2 className="organization-title text-2xl md:text-3xl font-extrabold tracking-tight text-white mb-1">
                  {t('organizationName')}
                </h2>
                <p className="organization-subtitle text-base md:text-lg text-white/70 font-medium max-w-2xl">
                  {t('organizationSubtitle')}
                </p>
              </div>
            </div>

            <div className="footer-logos flex items-center justify-center gap-5">
              <img src="/logo_armoirie-white.png" alt="Royaume du Maroc" className="h-[2.8rem] md:h-[3.92rem] w-auto" />
              <img src="/logo-commission-fr-white.png" alt="Comité National - CNCLT" className="h-[2.8rem] md:h-[3.92rem] w-auto" />
            </div>
          </div>

          {/* Decorative Divider */}
          <div className="relative h-px w-full mb-16 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
          </div>

          {/* Main Content Grid */}
          <div id="footer-main" className="footer-main mb-12 md:mb-16">
            <div className="footer-grid grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8 lg:grid-cols-[repeat(4,auto)] lg:justify-between lg:gap-8">
              {/* Contact Info */}
              <address className="footer-column animate-fade-in-up not-italic" style={{ animationDelay: '200ms' }} aria-labelledby="contact-heading">
                <h3 id="contact-heading" className={`column-title text-xl font-bold text-white mb-6 ${isRTL ? 'md:text-right' : 'md:text-left'}`}>{t('contact')}</h3>
                <div className="contact-info space-y-4">
                  <div className={`contact-item flex items-start gap-4`}>
                    <div className="w-10 h-10 rounded-lg glass-card flex items-center justify-center flex-shrink-0 text-orange-400">
                      <Phone className="w-5 h-5" />
                    </div>
                    <div className={`flex flex-col ${isRTL ? 'items-start text-right' : 'items-start text-left'}`}>
                      <span className="text-[10px] text-white/40 font-bold uppercase tracking-wider mb-1">{t('phone')}</span>
                      <a href="tel:0800004747" className="text-lg font-semibold text-white hover:text-orange-400 transition-colors" dir="ltr">0800 00 47 47</a>
                    </div>
                  </div>
                  <div className={`contact-item flex items-start gap-4`}>
                    <div className="w-10 h-10 rounded-lg glass-card flex items-center justify-center flex-shrink-0 text-orange-400">
                      <Mail className="w-5 h-5" />
                    </div>
                    <div className={`flex flex-col min-w-0 ${isRTL ? 'items-start text-right' : 'items-start text-left'}`}>
                      <span className="text-[10px] text-white/40 font-bold uppercase tracking-wider mb-1">{t('email')}</span>
                      <a href="mailto:cnclt-signalement@justice.gov.ma" className="text-sm font-semibold text-white hover:text-orange-400 transition-colors break-all">cnclt-signalement@justice.gov.ma</a>
                    </div>
                  </div>
                  <div className={`contact-item flex items-start gap-4`}>
                    <div className="w-10 h-10 rounded-lg glass-card flex items-center justify-center flex-shrink-0 text-orange-400">
                      <MapPin className="w-5 h-5" />
                    </div>
                    <div className={`flex flex-col ${isRTL ? 'items-start text-right' : 'items-start text-left'}`}>
                      <span className="text-[10px] text-white/40 font-bold uppercase tracking-wider mb-1">{t('address')}</span>
                      <span className="text-sm font-medium text-white/80 leading-relaxed">{t('addressValue')}</span>
                    </div>
                  </div>
                </div>
              </address>

              {/* Quick Links */}
              <nav className="footer-column animate-fade-in-up" style={{ animationDelay: '0ms' }} aria-labelledby="quick-links-heading">
                <h3 id="quick-links-heading" className={`column-title text-lg font-semibold tracking-wide text-white mb-4 text-center text-shadow-[0_1px_2px_rgba(0,0,0,0.1)] ${isRTL ? 'md:text-right' : 'md:text-left'}`}>{t('quickLinks')}</h3>
                {/* eslint-disable-next-line jsx-a11y/no-redundant-roles -- Tailwind preflight sets list-style:none on ul, which strips the implicit list role in Safari/VoiceOver. This restores it. */}
                <ul className="footer-links" role="list">
                  <li><Link to="/request-help" className={`footer-link inline-block min-h-[44px] flex items-center text-[0.9375rem] leading-[1.8] text-[rgba(255,255,255,0.8)] hover:text-[#FFA500] hover:underline decoration-[#FFA500] transition-all duration-200 ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-400 rounded px-2 text-shadow-[0_1px_2px_rgba(0,0,0,0.1)] ${isRTL ? 'hover:-translate-x-1' : 'hover:translate-x-1'}`} aria-label={t('requestHelp')}>{t('requestHelp')}</Link></li>
                  <li><Link to="/services" className={`footer-link inline-block min-h-[44px] flex items-center text-[0.9375rem] leading-[1.8] text-[rgba(255,255,255,0.8)] hover:text-[#FFA500] hover:underline decoration-[#FFA500] transition-all duration-200 ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-400 rounded px-2 text-shadow-[0_1px_2px_rgba(0,0,0,0.1)] ${isRTL ? 'hover:-translate-x-1' : 'hover:translate-x-1'}`} aria-label={t('servicesMap')}>{t('servicesMap')}</Link></li>
                  <li><Link to="/faq" className={`footer-link inline-block min-h-[44px] flex items-center text-[0.9375rem] leading-[1.8] text-[rgba(255,255,255,0.8)] hover:text-[#FFA500] hover:underline decoration-[#FFA500] transition-all duration-200 ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-400 rounded px-2 text-shadow-[0_1px_2px_rgba(0,0,0,0.1)] ${isRTL ? 'hover:-translate-x-1' : 'hover:translate-x-1'}`} aria-label={t('faq')}>{t('faq')}</Link></li>
                  <li><Link to="/about" className={`footer-link inline-block min-h-[44px] flex items-center text-[0.9375rem] leading-[1.8] text-[rgba(255,255,255,0.8)] hover:text-[#FFA500] hover:underline decoration-[#FFA500] transition-all duration-200 ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-400 rounded px-2 text-shadow-[0_1px_2px_rgba(0,0,0,0.1)] ${isRTL ? 'hover:-translate-x-1' : 'hover:translate-x-1'}`} aria-label={t('about')}>{t('about')}</Link></li>
                </ul>
              </nav>

              {/* Emergency Numbers */}
              <div className="footer-column animate-fade-in-up" style={{ animationDelay: '300ms' }} aria-labelledby="emergency-heading">
                <h3 id="emergency-heading" className={`column-title text-lg font-semibold tracking-wide text-white mb-4 text-center text-shadow-[0_1px_2px_rgba(0,0,0,0.1)] ${isRTL ? 'md:text-right' : 'md:text-left'}`}>{t('emergency')}</h3>
                <div className="emergency-numbers space-y-3" role="list" aria-label="Liste des numéros d'urgence">
                  <div className={`emergency-item group relative overflow-hidden cursor-pointer glass-card rounded-xl p-4 border-orange-500/30 hover:bg-white/10 hover:border-orange-500 transition-all focus-within:ring-2 focus-within:ring-blue-400 focus-within:ring-offset-2 focus-within:ring-offset-blue-700 ${isRTL ? 'border-r-4 text-right' : 'border-l-4 text-left'}`} onClick={(e) => handleRipple(0, e)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleRipple(0, e as any); }} aria-label="Police - Appeler le 19">
                    {ripplePosition[0] && (
                      <span
                        className="absolute rounded-full bg-white/30 pointer-events-none"
                        style={{
                          left: ripplePosition[0].x - 150,
                          top: ripplePosition[0].y - 150,
                          width: 300,
                          height: 300,
                          animation: 'ripple 0.6s ease-out',
                        }}
                      />
                    )}
                    <div className={`flex items-center gap-4`}>
                      <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110">
                        <Shield className="w-5 h-5 text-orange-400 group-hover:text-orange-300 transition-colors" />
                      </div>
                      <div className={`flex flex-col min-w-0 ${isRTL ? 'items-start text-right' : 'items-start text-left'}`}>
                        <span className="text-[10px] text-white/40 font-bold uppercase tracking-widest leading-none mb-1.5">{t('police')}</span>
                        <a href="tel:19" className="text-xl font-black text-orange-400 group-hover:text-white transition-colors leading-none" aria-label="Composer le 19" dir="ltr">19</a>
                      </div>
                    </div>
                  </div>
                  <div className={`emergency-item group relative overflow-hidden cursor-pointer glass-card rounded-xl p-4 border-orange-500/30 hover:bg-white/10 hover:border-orange-500 transition-all focus-within:ring-2 focus-within:ring-blue-400 focus-within:ring-offset-2 focus-within:ring-offset-blue-700 ${isRTL ? 'border-r-4 text-right' : 'border-l-4 text-left'}`} onClick={(e) => handleRipple(1, e)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleRipple(1, e as any); }} aria-label="Gendarmerie - Appeler le 177">
                    {ripplePosition[1] && (
                      <span
                        className="absolute rounded-full bg-white/30 pointer-events-none"
                        style={{
                          left: ripplePosition[1].x - 150,
                          top: ripplePosition[1].y - 150,
                          width: 300,
                          height: 300,
                          animation: 'ripple 0.6s ease-out',
                        }}
                      />
                    )}
                    <div className={`flex items-center gap-4`}>
                      <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110">
                        <AlertCircle className="w-5 h-5 text-orange-400 group-hover:text-orange-300 transition-colors" />
                      </div>
                      <div className={`flex flex-col min-w-0 ${isRTL ? 'items-start text-right' : 'items-start text-left'}`}>
                        <span className="text-[10px] text-white/40 font-bold uppercase tracking-widest leading-none mb-1.5">{t('gendarmerie')}</span>
                        <a href="tel:177" className="text-xl font-black text-orange-400 group-hover:text-white transition-colors leading-none" aria-label="Composer le 177" dir="ltr">177</a>
                      </div>
                    </div>
                  </div>
                  <div className={`emergency-item group relative overflow-hidden cursor-pointer glass-card rounded-xl p-4 border-orange-500/30 hover:bg-white/10 hover:border-orange-500 transition-all focus-within:ring-2 focus-within:ring-blue-400 focus-within:ring-offset-2 focus-within:ring-offset-blue-700 ${isRTL ? 'border-r-4 text-right' : 'border-l-4 text-left'}`} onClick={(e) => handleRipple(2, e)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleRipple(2, e as any); }} aria-label="Urgences médicales - Appeler le 15">
                    {ripplePosition[2] && (
                      <span
                        className="absolute rounded-full bg-white/30 pointer-events-none"
                        style={{
                          left: ripplePosition[2].x - 150,
                          top: ripplePosition[2].y - 150,
                          width: 300,
                          height: 300,
                          animation: 'ripple 0.6s ease-out',
                        }}
                      />
                    )}
                    <div className={`flex items-center gap-4`}>
                      <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110">
                        <Activity className="w-5 h-5 text-orange-400 group-hover:text-orange-300 transition-colors" />
                      </div>
                      <div className={`flex flex-col min-w-0 ${isRTL ? 'items-start text-right' : 'items-start text-left'}`}>
                        <span className="text-[10px] text-white/40 font-bold uppercase tracking-widest leading-none mb-1.5">{t('medical')}</span>
                        <a href="tel:15" className="text-xl font-black text-orange-400 group-hover:text-white transition-colors leading-none" aria-label="Composer le 15" dir="ltr">15</a>
                      </div>
                    </div>
                  </div>
                  <div className={`emergency-item group relative overflow-hidden cursor-pointer animate-pulse-custom glass-card rounded-xl p-4 border-orange-500/40 hover:bg-white/10 hover:border-orange-500 transition-all focus-within:ring-2 focus-within:ring-blue-400 focus-within:ring-offset-2 focus-within:ring-offset-blue-700 ${isRTL ? 'border-r-4 text-right' : 'border-l-4 text-left'}`} onClick={(e) => handleRipple(3, e)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleRipple(3, e as any); }} aria-label="Ligne Anti-Trafic - Appeler le 0800004747">
                    <span className="sr-only">Numéro d'urgence prioritaire</span>
                    {ripplePosition[3] && (
                      <span
                        className="absolute rounded-full bg-white/30 pointer-events-none"
                        style={{
                          left: ripplePosition[3].x - 150,
                          top: ripplePosition[3].y - 150,
                          width: 300,
                          height: 300,
                          animation: 'ripple 0.6s ease-out',
                        }}
                      />
                    )}
                    <div className={`flex items-center gap-4`}>
                      <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110">
                        <Phone className="w-5 h-5 text-orange-400 group-hover:text-orange-300 transition-colors" />
                      </div>
                      <div className={`flex flex-col min-w-0 ${isRTL ? 'items-start text-right' : 'items-start text-left'}`}>
                        <span className="text-[10px] text-white/40 font-bold uppercase tracking-widest leading-none mb-1.5">{t('antiTraffic')}</span>
                        <a href="tel:0800004747" className="text-lg md:text-xl font-black text-orange-400 group-hover:text-white transition-colors leading-none" aria-label="Composer le 0800004747" dir="ltr">0800 00 47 47</a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Social Media */}
              <nav className="footer-column animate-fade-in-up" style={{ animationDelay: '400ms' }} aria-labelledby="social-heading">
                <h3 id="social-heading" className={`column-title text-lg font-semibold tracking-wide text-white mb-4 text-center text-shadow-[0_1px_2px_rgba(0,0,0,0.1)] ${isRTL ? 'md:text-right' : 'md:text-left'}`}>{t('followUs')}</h3>
                <ul ref={socialIconsRef} role="list" className={`social-links flex flex-col gap-3 ${isVisible ? 'animate-scale-in' : 'opacity-0 scale-[0.8]'}`}>
                  <li>
                    <span className="flex items-center gap-4 cursor-default" aria-disabled="true">
                      <span className="w-10 h-10 rounded-lg glass-card flex items-center justify-center flex-shrink-0 text-orange-400">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                        </svg>
                      </span>
                      <span className="text-[0.9375rem] text-[rgba(255,255,255,0.8)]">Facebook</span>
                    </span>
                  </li>
                  <li>
                    <span className="flex items-center gap-4 cursor-default" aria-disabled="true">
                      <span className="w-10 h-10 rounded-lg glass-card flex items-center justify-center flex-shrink-0 text-orange-400">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 6.62 5.367 11.987 11.988 11.987 6.62 0 11.987-5.367 11.987-11.987C24.014 5.367 18.637.001 12.017.001zM8.449 16.988c-1.297 0-2.448-.49-3.323-1.297C4.198 14.895 3.708 13.744 3.708 12.447s.49-2.448 1.297-3.323c.875-.807 2.026-1.297 3.323-1.297s2.448.49 3.323 1.297c.807.875 1.297 2.026 1.297 3.323s-.49 2.448-1.297 3.323c-.875.807-2.026 1.297-3.323 1.297zm7.83-9.281c-.49 0-.98-.49-.98-.98s.49-.98.98-.98.98.49.98.98-.49.98-.98.98z" />
                        </svg>
                      </span>
                      <span className="text-[0.9375rem] text-[rgba(255,255,255,0.8)]">Instagram</span>
                    </span>
                  </li>
                  <li>
                    <a href="https://www.youtube.com/@CNCLT-justice" target="_blank" rel="noopener noreferrer" className="group flex items-center gap-4 rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-400" aria-label="YouTube">
                      <span className="w-10 h-10 rounded-lg glass-card flex items-center justify-center flex-shrink-0 text-orange-400 transition-colors group-hover:bg-white/20">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                        </svg>
                      </span>
                      <span className={`text-[0.9375rem] text-[rgba(255,255,255,0.8)] group-hover:text-[#FFA500] group-hover:underline decoration-[#FFA500] transition-all duration-200 ease-in-out ${isRTL ? 'group-hover:-translate-x-1' : 'group-hover:translate-x-1'}`}>YouTube</span>
                    </a>
                  </li>
                  <li>
                    <a href="https://x.com/CncltJustice" target="_blank" rel="noopener noreferrer" className="group flex items-center gap-4 rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-400" aria-label="X">
                      <span className="w-10 h-10 rounded-lg glass-card flex items-center justify-center flex-shrink-0 text-orange-400 transition-colors group-hover:bg-white/20">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                        </svg>
                      </span>
                      <span className={`text-[0.9375rem] text-[rgba(255,255,255,0.8)] group-hover:text-[#FFA500] group-hover:underline decoration-[#FFA500] transition-all duration-200 ease-in-out ${isRTL ? 'group-hover:-translate-x-1' : 'group-hover:translate-x-1'}`}>X</span>
                    </a>
                  </li>
                </ul>
              </nav>
            </div>
          </div>

          {/* Footer Bottom */}
          <div className="footer-bottom border-t border-white/10 pt-8">
            <p className="partnership-notice text-center text-xs md:text-sm leading-relaxed text-white/60 max-w-4xl mx-auto mb-8">
              {t('partnershipNotice')}
            </p>

            {/* Divider */}
            <div className="h-px bg-white opacity-10 mb-8"></div>

            <div className="flex flex-col items-center justify-center gap-4 pb-0 mb-0">
              <p className="copyright text-base md:text-lg font-bold text-white/90 text-center">
                &copy; {new Date().getFullYear()} {t('organizationName')} {t('organizationSubtitle')}. {t('allRightsReserved')}
              </p>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;
